import CertifiedDocumentsSlider, {
  ICertifiedDocumentsSliderProps,
} from "@components/CertifiedDocuments/CertifiedDocumentsSlider";
import { TCertificateStatus } from "@components/CertifiedDocuments/CertifiedDocumentsSlider/CertifiedDocumentCard";
import { GraduatedIcon } from "@cpj-ai/panel-npm-package";
import { GetEmployeeByIdQuery } from "@graphql/generated";
import { useMemo } from "react";
import CertificateDetailModal from "./CertifiedDocumentsSlider/CertificateDetailModal";

type TJobSeekerInfo = NonNullable<
  NonNullable<NonNullable<NonNullable<GetEmployeeByIdQuery["organization"]>["employee"]>["get"]>["employee"]
>;

export interface ICertifiedDocument {
  title?: string | null;
  issuer?: string | null;
  date?: {
    from?: string | null;
    to?: string | null;
  };
  status?: TCertificateStatus;
  file?: string;
  link?: string;
}

interface ICertifiedDocumentsTabContentProps {
  licenses: TJobSeekerInfo["certificateandlicenses"];
  educations: TJobSeekerInfo["educations"];
  languageCertificates: TJobSeekerInfo["languagecertificates"];
  workExperiences: TJobSeekerInfo["workexperiences"];
  sliderBreakpoints: ICertifiedDocumentsSliderProps["sliderBreakpoints"];
}

const STATUS_MAP: Record<string, TCertificateStatus> = {
  DRAFTED: "NOT_VERIFIED",
  SUBMITTED: "NOT_VERIFIED",
  REJECTED: "REJECTED",
  SELF_VERIFIED: "VERIFIED_BY_JOB_SEEKER",
  VERIFIED: "VERIFIED",
} as const;

const CertifiedDocumentsTabContent = ({
  educations,
  languageCertificates,
  licenses,
  workExperiences,
  sliderBreakpoints,
}: ICertifiedDocumentsTabContentProps) => {
  const getStatus = (status: string | null | undefined): TCertificateStatus =>
    status ? (STATUS_MAP[status] ?? "NOT_VERIFIED") : "NOT_VERIFIED";

  const workExperiencesItems = useMemo(
    () =>
      workExperiences?.filter(Boolean).map(
        (workExperience): ICertifiedDocument => ({
          title: workExperience?.jobTitle,
          issuer: workExperience?.organization,
          date: { from: workExperience?.start, to: workExperience?.end },
          status: getStatus(workExperience?.status),
          file:
            workExperience?.employerlettermethod?.employerLetter.file ?? workExperience?.paystubsmethod?.paystubs.file,
        }),
      ) ?? [],
    [workExperiences],
  );

  const educationsItems = useMemo(
    () =>
      educations?.filter(Boolean).map(
        (education): ICertifiedDocument => ({
          title: education?.field.name,
          issuer: education?.university.name,
          date: { from: education?.start, to: education?.end },
          status: getStatus(education?.status),
          file:
            education?.communicationmethod?.degreeFile.file ?? education?.ieemethod?.educationEvaluationDocument.file,
        }),
      ) ?? [],
    [educations],
  );

  const languageCertificatesItems = useMemo(
    () =>
      languageCertificates?.filter(Boolean).map(
        (language): ICertifiedDocument => ({
          title: language?.language,
          issuer: language?.test?.title,
          date: { from: language?.issuedAt, to: language?.expiredAt },
          status: getStatus(language?.status),
          file: language?.offlinemethod?.certificateFile.file,
          link: language?.onlinemethod?.certificateLink,
        }),
      ) ?? [],
    [languageCertificates],
  );

  const licensesItems = useMemo(
    () =>
      licenses?.filter(Boolean).map(
        (license): ICertifiedDocument => ({
          title: license?.title,
          issuer: license?.certifier,
          date: { from: license?.issuedAt, to: license?.expiredAt },
          status: getStatus(license?.status),
          file: license?.certificateandlicenseofflineverificationmethod?.certificateFile.file,
          link: license?.certificateandlicenseonlineverificationmethod?.certificateLink,
        }),
      ) ?? [],
    [licenses],
  );

  const sliderConfig = useMemo(
    () => [
      { title: "Work Experience", items: workExperiencesItems },
      { title: "Education", items: educationsItems },
      { title: "Language", items: languageCertificatesItems },
      { title: "Certificate", items: licensesItems },
    ],
    [workExperiencesItems, educationsItems, languageCertificatesItems, licensesItems],
  );

  return (
    <div className="flex flex-col gap-8">
      {sliderConfig.map(({ title, items }) => (
        <CertifiedDocumentsSlider
          key={title}
          title={title}
          icon={GraduatedIcon}
          certifiedDocuments={items}
          sliderBreakpoints={sliderBreakpoints}
        />
      ))}
      <CertificateDetailModal />
    </div>
  );
};

export default CertifiedDocumentsTabContent;
